import React, { useEffect, useRef, useState } from 'react';
import VideoGrid from './VideoGrid';
import './SearchResultsSidebar.css';

const SearchResultsSidebar = ({
    videos,
    loading,
    onVideoSelect,
    selectedVideo,
    query,
    isVisible,
    onClose,
    onCollapse,
    page = 1,
    hasPrevious = false,
    hasNext = false,
    onPageChange,
    targetScreen = null
}) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const contentRef = useRef(null);
    const scrollPositionRef = useRef(0);

    useEffect(() => {
        if (!loading) return;
        scrollPositionRef.current = 0;
        contentRef.current?.scrollTo({ top: 0 });
    }, [loading, page]);

    useEffect(() => {
        if (!isVisible || !contentRef.current) return;
        contentRef.current.scrollTop = scrollPositionRef.current;
    }, [isVisible]);

    if (!isVisible) return null;

    const toggleCollapse = () => {
        const newCollapsedState = !isCollapsed;
        setIsCollapsed(newCollapsedState);
        if (onCollapse) {
            onCollapse(newCollapsedState);
        }
    };

    return (
        <aside
            className={`search-results-sidebar ${isCollapsed ? 'collapsed' : ''}`}
            aria-label="Search results"
        >
            <div className="search-results-header">
                <h3 className="search-results-title">
                    <span>{loading ? 'Searching' : 'Results'}</span>
                    <span className="results-separator" aria-hidden="true">/</span>
                    <span className="results-count-badge">{loading ? '…' : videos.length}</span>
                </h3>
                <div className="search-results-controls">
                    <button 
                        className="sidebar-control-btn"
                        onClick={toggleCollapse}
                        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            {isCollapsed ? (
                                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                            ) : (
                                <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                            )}
                        </svg>
                    </button>
                    <button 
                        className="sidebar-control-btn"
                        onClick={onClose}
                        aria-label="Close sidebar"
                        title="Close sidebar"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                    </button>
                </div>
            </div>

            {!isCollapsed && targetScreen && (
                <div className="search-target-status" role="status" aria-live="polite">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <rect x="3.5" y="4.5" width="17" height="15" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M8 19.5h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>Next video goes to <strong>Screen {targetScreen}</strong></span>
                </div>
            )}
            
            <div
                ref={contentRef}
                className={`search-results-content ${isCollapsed ? 'hidden' : ''}`}
                onScroll={(event) => {
                    scrollPositionRef.current = event.currentTarget.scrollTop;
                }}
            >
                {loading ? (
                    <div className="loading-container" role="status">
                        <div className="modern-spinner"></div>
                        <span>Searching…</span>
                    </div>
                ) : (
                    <VideoGrid 
                        videos={videos || []}
                        loading={false}
                        onVideoSelect={onVideoSelect}
                        selectedVideo={selectedVideo}
                    />
                )}
            </div>

            {!isCollapsed && (hasPrevious || hasNext) && (
                <nav className="search-pagination" aria-label={`Search results pages for ${query}`}>
                    <button
                        type="button"
                        className="search-pagination-button"
                        disabled={loading || !hasPrevious}
                        onClick={() => onPageChange?.(page - 1)}
                        aria-label="Go to previous results page"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                        </svg>
                        <span className="search-pagination-label">Previous</span>
                    </button>
                    <span className="search-pagination-page" aria-live="polite">
                        Page {page}
                    </span>
                    <button
                        type="button"
                        className="search-pagination-button"
                        disabled={loading || !hasNext}
                        onClick={() => onPageChange?.(page + 1)}
                        aria-label="Go to next results page"
                    >
                        <span className="search-pagination-label">Next</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M8.59 16.59 10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                        </svg>
                    </button>
                </nav>
            )}
        </aside>
    );
};

export default SearchResultsSidebar; 